// CD: backend
import { defineOnce, fetchSectionDocument, showToast, emit, EVENTS, routesRoot, prefersReducedMotion, keyboardInteraction, trackInputModality } from './pushe-ui.js';

export function resolveSelection(optionsWithValues = [], selectedValueIds = []) {
  const ids = new Set(Array.from(selectedValueIds, String));
  const options = [...optionsWithValues].sort((a, b) => a.position - b.position);
  const soldOutValueIds = new Set();
  const selected = options.map(option => {
    for (const value of option.values) if (value.available === false) soldOutValueIds.add(String(value.id));
    return option.values.find(value => ids.has(String(value.id))) ?? (option.values.length === 1 ? option.values[0] : null);
  });
  // Shopify resolves each value against the preceding options. The LAST option describes the full combination.
  const variantId = selected.length && selected.every(Boolean) ? selected.at(-1).variantId ?? null : null;
  return { variantId, available: variantId !== null && selected.every(value => value.available === true), soldOutValueIds };
}

export function cmToIn(value) { return Math.round(value / 2.54 * 2) / 2; }

// Both the product modal and the standalone guide render the same table and use canonical cm rows.
export function setChartUnit(chart, data, unit) {
  if (!chart || !data) return;
  const rows = data.rows ?? [];
  [...chart.querySelectorAll('tbody tr')].forEach((row, rowIndex) => {
    [...row.querySelectorAll('td')].forEach((cell, index) => {
      const value = rows[rowIndex]?.values?.[index];
      if (typeof value === 'number' && Number.isFinite(value) && value >= 0) cell.textContent = String(unit === 'in' ? cmToIn(value) : Math.round(value * 10) / 10);
    });
  });
  [...chart.querySelectorAll('.pushe-chart__unit')].forEach((button, index) => button.setAttribute('aria-pressed', String(index === (unit === 'in' ? 1 : 0))));
  const caption = chart.querySelector('caption');
  const captionKey = unit === 'in' ? 'captionIn' : 'captionCm';
  if (caption) caption.textContent = data[captionKey];
}

function rememberedChartUnit() {
  try { return sessionStorage.getItem('pushe-chart-unit') === 'in' ? 'in' : 'cm'; } catch { return 'cm'; }
}

function pickChartUnit(button, chart, data) {
  const unit = [...chart.querySelectorAll('.pushe-chart__unit')].indexOf(button) === 1 ? 'in' : 'cm';
  setChartUnit(chart, data, unit);
  try { sessionStorage.setItem('pushe-chart-unit', unit); } catch { /* Storage is optional. */ }
}

async function cartRequest(url, init = {}, signal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 10000);
  try {
    const response = await fetch(url, { ...init, signal: controller.signal, headers: { Accept: 'application/json', ...init.headers } });
    return { response, body: await response.json() };
  } finally {
    clearTimeout(timeout); signal?.removeEventListener('abort', abort);
  }
}

export async function addToCart({ root, variantId, quantity = 1, signal }) {
  const base = root.endsWith('/') ? root : `${root}/`;
  try {
    const { response, body } = await cartRequest(`${base}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    }, signal);
    if (response.status === 422) return { ok: false, kind: 'stock', message: body.description };
    if (!response.ok) throw new Error('cart_add_failed');
    let itemCount = body.item_count;
    if (!Number.isInteger(itemCount) || itemCount < 0) {
      const cart = await cartRequest(`${base}cart.js`, { cache: 'no-store' }, signal);
      if (!cart.response.ok) throw new Error('cart_read_failed');
      itemCount = cart.body.item_count;
    }
    if (!Number.isInteger(itemCount) || itemCount < 0) throw new Error('cart_count_missing');
    return { ok: true, itemCount, sections: body.sections };
  } catch {
    // A lost response may have committed. Reconcile, but never infer that this particular add succeeded.
    try {
      const cart = await cartRequest(`${base}cart.js`, { cache: 'no-store' });
      if (cart.response.ok && Number.isInteger(cart.body?.item_count) && cart.body.item_count >= 0) {
        return { ok: false, kind: 'unconfirmed', cart: cart.body };
      }
    } catch { /* A failed read never makes it safe to retry the mutation. */ }
    return { ok: false, kind: 'unconfirmed' };
  }
}

const BaseElement = globalThis.HTMLElement ?? class {};
export class PusheProduct extends BaseElement {
  connectedCallback() {
    if (this.lifecycle) return;
    trackInputModality(this.ownerDocument);
    this.lifecycle = new AbortController();
    this.readData();
    if (this.dataset.unconfirmed) this.setBuyState('unconfirmed');
    this.changeHandler = event => this.changeOption(event);
    this.submitHandler = event => this.submitProduct(event);
    this.clickHandler = event => this.clickProduct(event);
    this.scrollHandler = event => { if (event.target.matches?.('.pushe-gallery__main')) this.syncGallery(); };
    this.chartErrorHandler = event => {
      const image = event.target;
      if (!image.matches?.('.pushe-chart__scroll img')) return;
      image.dataset.failed = 'true';
      const dialog = image.closest('pushe-dialog');
      if (dialog?.isOpen) {
        dialog.close();
        showToast({ message: this.data.networkError, tone: 'error' });
      }
    };
    this.addEventListener('change', this.changeHandler);
    this.addEventListener('submit', this.submitHandler);
    this.addEventListener('click', this.clickHandler);
    this.addEventListener('scroll', this.scrollHandler, true);
    this.addEventListener('error', this.chartErrorHandler, true);
    this.prepareGallery();
    this.setChartUnit(rememberedChartUnit());
  }
  disconnectedCallback() {
    if (this.adding) this.dataset.unconfirmed = 'true';
    this.lifecycle?.abort(); this.selectionRequest?.abort(); clearTimeout(this.addedTimer);
    this.removeEventListener('change', this.changeHandler);
    this.removeEventListener('submit', this.submitHandler);
    this.removeEventListener('click', this.clickHandler);
    this.removeEventListener('scroll', this.scrollHandler, true);
    this.removeEventListener('error', this.chartErrorHandler, true);
    this.lifecycle = null; this.selectionRequest = null; this.adding = false; this.selecting = false; this.pendingSelection = null;
    this.setBuyState('idle');
  }
  readData() {
    try { this.data = JSON.parse(this.querySelector('[data-pushe-product]')?.textContent ?? '{}'); }
    catch { this.data = {}; }
    this.selected = new Map();
    for (const option of this.data.options_with_values ?? []) {
      const value = option.values.find(value => value.selected) ?? (option.values.length === 1 ? option.values[0] : null);
      if (value) this.selected.set(`option-${option.position}`, value.name);
    }
    this.restoreRadios();
  }
  restoreRadios() {
    for (const radio of this.querySelectorAll('input[type="radio"][name^="option-"]')) radio.checked = this.selected.get(radio.name) === radio.value;
  }
  setBuyState(state) {
    if (this.dataset.unconfirmed) state = 'unconfirmed';
    const part = this.querySelector('[data-pushe-part="buy"]');
    const template = part?.querySelector(`template[data-pushe-buy="${state}"]`);
    const current = part?.querySelector('.pushe-buy');
    if (!template?.content.firstElementChild || !current) return;
    const focused = current.contains(document.activeElement);
    const replacement = template.content.firstElementChild.cloneNode(true);
    replacement.dataset.state = state;
    current.replaceWith(replacement);
    if (focused) replacement.querySelector('button')?.focus();
  }
  async changeOption(event) {
    const keyboard = keyboardInteraction(event);
    const radio = event.target;
    if (!radio.matches?.('input[type="radio"][name^="option-"]')) return;
    const option = (this.data.options_with_values ?? []).find(option => `option-${option.position}` === radio.name);
    const value = option?.values.find(value => value.name === radio.value);
    if (!value || value.available === false || radio.disabled || radio.getAttribute('aria-disabled') === 'true' || this.adding) {
      event.preventDefault(); this.restoreRadios(); return;
    }
    const chosen = new Map(this.pendingSelection?.values ?? this.selected); chosen.set(radio.name, value.name);
    const ids = (this.data.options_with_values ?? []).map(option => option.values.find(value => value.name === chosen.get(`option-${option.position}`))?.id);
    if (ids.some(id => id == null)) { this.restoreRadios(); return; }
    const productUrl = this.productUrl || window.location.pathname || this.data.url;
    const targetUrl = value.product_url || this.pendingSelection?.url || productUrl;
    this.pendingSelection = { values: chosen, url: targetUrl };
    this.selectionRequest?.abort();
    const controller = new AbortController(); this.selectionRequest = controller;
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 10000);
    this.selecting = true; clearTimeout(this.addedTimer); this.setBuyState('pending');
    try {
      const url = new URL(targetUrl, window.location.origin);
      url.searchParams.delete('variant'); url.searchParams.set('option_values', ids.join(','));
      const next = await fetchSectionDocument(this.dataset.sectionId || 'pushe-main-product', url.href, { signal: controller.signal });
      if (controller.signal.aborted) return;
      const product = next.querySelector('pushe-product');
      const dataNode = product?.querySelector('[data-pushe-product]');
      if (!product || !dataNode) throw new Error('product_section_missing');
      JSON.parse(dataNode.textContent);
      const parts = [...this.querySelectorAll('[data-pushe-part]')];
      const replacements = parts.map(part => product.querySelector(`[data-pushe-part="${part.dataset.pushePart}"]`));
      if (replacements.some(part => !part)) throw new Error('product_part_missing');
      parts.forEach((part, index) => part.replaceWith(replacements[index].cloneNode(true)));
      this.readData(); this.setBuyState('idle'); this.prepareGallery(true, keyboard);
      this.setChartUnit(rememberedChartUnit());
      const current = new URL(url.href);
      current.searchParams.delete('option_values');
      if (this.data.variantId != null) current.searchParams.set('variant', this.data.variantId);
      else current.searchParams.set('option_values', ids.join(','));
      window.history.replaceState({}, '', current.href);
      this.productUrl = current.pathname;
      const focus = [...this.querySelectorAll('input[type="radio"]')].find(input => input.name === radio.name && input.value === radio.value);
      focus?.focus();
    } catch {
      if (this.selectionRequest === controller && (!controller.signal.aborted || timedOut)) {
        this.restoreRadios(); this.setBuyState('idle'); showToast({ message: this.data.networkError, tone: 'error' });
      }
    } finally {
      clearTimeout(timeout);
      if (this.selectionRequest === controller) { this.selecting = false; this.selectionRequest = null; this.pendingSelection = null; }
    }
  }
  async submitProduct(event) {
    if (!event.target.matches?.('form[data-pushe-product-form]')) return;
    event.preventDefault();
    if (this.adding || this.selecting || this.dataset.unconfirmed || !this.data.available || this.data.variantId == null) return;
    const lifecycle = this.lifecycle;
    this.adding = true; clearTimeout(this.addedTimer); this.setBuyState('pending');
    const title = [this.data.title, this.data.variantTitle].filter(Boolean).join(' · ');
    const result = await addToCart({ root: routesRoot(), variantId: this.data.variantId, signal: lifecycle.signal });
    if (result.kind === 'unconfirmed') {
      this.dataset.unconfirmed = 'true';
      this.setBuyState('unconfirmed');
    }
    if (result.cart) emit(EVENTS.CART_UPDATED, { item_count: result.cart.item_count });
    else if (result.ok) emit(EVENTS.CART_UPDATED, { item_count: result.itemCount });
    if (lifecycle.signal.aborted) return;
    this.adding = false;
    if (result.ok) {
      showToast({ templateId: 'pushe-toast-added-template', vars: { name: title } });
      this.setBuyState('added'); this.addedTimer = setTimeout(() => this.setBuyState('idle'), 2000);
    } else {
      showToast({ message: result.message || (result.kind === 'unconfirmed' ? this.data.networkError : this.data.addError), tone: 'error' });
      this.setBuyState('error');
    }
  }
  clickProduct(event) {
    const guide = event.target.closest?.('.pushe-opt__guide');
    if (guide && this.contains(guide)) {
      const dialog = this.querySelector('pushe-dialog');
      const image = dialog?.querySelector('.pushe-chart__scroll img');
      const failed = image?.dataset.failed === 'true' || (image?.complete && image.naturalWidth === 0);
      if (this.data.chartImage === true && image && !failed && typeof dialog.open === 'function') { event.preventDefault(); dialog.open(guide); }
    }
    const dot = event.target.closest?.('.pushe-gallery__dot, .pushe-gallery__thumb');
    if (dot && this.contains(dot)) {
      const strip = this.querySelector('.pushe-gallery__main');
      const buttons = [...this.querySelectorAll(dot.matches('.pushe-gallery__dot') ? '.pushe-gallery__dot' : '.pushe-gallery__thumb')];
      const slide = [...strip?.querySelectorAll('.pushe-gallery__slide') ?? []].filter(slide => !slide.hidden)[buttons.indexOf(dot)];
      if (slide) strip.scrollTo?.({ left: slide.offsetLeft - strip.offsetLeft, behavior: prefersReducedMotion() || keyboardInteraction(event) ? 'instant' : 'smooth' });
    }
    const unit = event.target.closest?.('.pushe-chart__unit');
    if (unit) {
      pickChartUnit(unit, this.querySelector('.pushe-chart'), this.data.chart);
    }
  }
  setChartUnit(unit) {
    setChartUnit(this.querySelector('.pushe-chart'), this.data.chart, unit);
  }
  prepareGallery(reveal = false, keyboard = false) {
    const strip = this.querySelector('.pushe-gallery__main');
    if (!strip) return;
    const slides = [...strip.querySelectorAll('.pushe-gallery__slide')].filter(slide => !slide.hidden);
    strip.dataset.pusheCount = String(slides.length);
    slides.forEach((slide, index) => { slide.dataset.pusheCol = slides.length === 1 ? 'full' : index % 2 ? 'narrow' : 'wide'; slide.dataset.pusheRow = String(Math.floor(index / 2)); });
    const index = Math.max(0, (this.data.imageIds ?? []).findIndex(id => String(id) === String(this.data.featuredImageId)));
    const slide = slides[index];
    if (slide) {
      const behavior = !reveal || prefersReducedMotion() || keyboard ? 'instant' : 'smooth';
      if (window.matchMedia?.('(min-width: 900px)').matches) {
        if (reveal) {
          const rect = slide.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) slide.scrollIntoView?.({ behavior, block: 'nearest' });
        }
      } else strip.scrollTo?.({ left: slide.offsetLeft - strip.offsetLeft, behavior });
    }
    this.syncGallery();
  }
  syncGallery() {
    const strip = this.querySelector('.pushe-gallery__main');
    const index = strip?.clientWidth ? Math.round(strip.scrollLeft / strip.clientWidth) : 0;
    [...this.querySelectorAll('.pushe-gallery__dot')].forEach((dot, i) => {
      dot.classList.toggle('pushe-gallery__dot--active', i === index);
      dot.setAttribute('aria-current', String(i === index));
    });
    [...this.querySelectorAll('.pushe-gallery__thumb')].forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
  }
}

export class PusheChartGuide extends BaseElement {
  connectedCallback() {
    if (this.clickHandler) return;
    try { this.chartData = JSON.parse(this.querySelector('[data-pushe-chart]')?.textContent ?? 'null'); } catch { this.chartData = null; }
    const chart = this.querySelector('.pushe-chart');
    setChartUnit(chart, this.chartData, rememberedChartUnit());
    this.clickHandler = event => {
      const button = event.target.closest?.('.pushe-chart__unit');
      if (button && chart?.contains(button)) pickChartUnit(button, chart, this.chartData);
    };
    this.addEventListener('click', this.clickHandler);
  }
  disconnectedCallback() { this.removeEventListener('click', this.clickHandler); this.clickHandler = null; }
}

export class PusheProductRecommendations extends BaseElement {
  connectedCallback() {
    if (this.controller || this.dataset.loaded === 'true') return;
    this.controller = new AbortController();
    if (typeof IntersectionObserver === 'undefined') { this.load(); return; }
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
    });
    this.observer.observe(this);
  }
  disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); this.controller = null; this.loading = false; }
  async load() {
    if (!this.dataset.url || this.loading) return;
    const controller = this.controller; this.loading = true;
    try {
      const doc = await fetchSectionDocument(this.dataset.sectionId, this.dataset.url, { signal: controller.signal });
      if (controller.signal.aborted) return;
      const content = doc.querySelector('pushe-product-recommendations');
      if (!content) throw new Error('recommendations_missing');
      this.replaceChildren(...[...content.childNodes].map(node => node.cloneNode(true)));
      this.dataset.loaded = 'true';
    } catch { /* Optional recommendations remain empty on a failed request. */ }
    finally { if (this.controller === controller) this.loading = false; }
  }
}
defineOnce('pushe-product', PusheProduct);
defineOnce('pushe-size-guide', PusheChartGuide);
defineOnce('pushe-product-recommendations', PusheProductRecommendations);
